// src/components/dashboard/AddGraphModal.tsx

import { useState } from "react";
import { Modal, Card, Spinner, Alert } from "react-bootstrap";
import http from "services/httpClient";

interface GraphTemplate {
  type: string;
  label: string;
  description: string;
  image: string;
}

interface AddGraphModalProps {
  show: boolean;
  handleClose: () => void;
  deviceId: number;
  refetchGraphs: () => void;
}

const graphTemplates: GraphTemplate[] = [
  {
    type: "standard_chart",
    label: "Standard Chart",
    description:
      "Display historical data for multiple parameters on a chart.",
    image: "/images/graph/standard_chart.png",
  },
  {
    type: "solid_gauge",
    label: "Solid Gauge",
    description:
      "Display the current numeric value of a parameter on a gauge.",
    image: "/images/graph/solid_gauge.png",
  },
  {
    type: "temperature_gauge",
    label: "Temperature Gauge",
    description:
      "Display temperature readings with a thermometer-style gauge.",
    image: "/images/graph/temperature_gauge.png",
  },
  {
    type: "windrose_chart",
    label: "Wind Rose",
    description:
      "Display directional data for a parameter on a wind rose chart.",
    image: "/images/graph/windrose_chart.png",
  },
  {
    type: "boolean_indicator",
    label: "Boolean Indicator",
    description:
      "Display digital ON/OFF status with configurable active-state logic.",
    image: "/images/graph/boolean_indicator.png",
  },
];

export function AddGraphModal({
  show,
  handleClose,
  deviceId,
  refetchGraphs,
}: AddGraphModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectGraph = async (template: GraphTemplate) => {
    if (loading) return;

    setLoading(true);
    setError(null);

    const newGraph = {
      device_id: deviceId,
      graph_config: {
        type: template.type,
        title: template.label,
        color: "green",

        // Boolean Indicator defaults:
        // 1 = active, 0 = inactive.
        // These values are configuration defaults only.
        ...(template.type === "boolean_indicator" && {
          active_value: 1,
          inactive_value: 0,
          active_label: "ON",
          inactive_label: "OFF",
        }),
      },
    };

    try {
      await http.post("/graphs", newGraph);

      await refetchGraphs();
      handleClose();
    } catch (err) {
      setError(
        "Unable to add the graph. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={loading ? undefined : handleClose}
      size="xl"
      centered
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>Choose a Graph Template</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <div
            className="d-flex flex-column justify-content-center
              align-items-center py-5 gap-3"
          >
            <Spinner animation="border" variant="primary" />
            <span>Adding graph...</span>
          </div>
        ) : (
          <div className="d-flex flex-wrap gap-3 justify-content-center">
            {graphTemplates.map((template) => (
              <Card
                key={template.type}
                role="button"
                tabIndex={0}
                aria-label={`Add ${template.label}`}
                className="text-center shadow-sm graph-template-card"
                style={{
                  width: "12rem",
                  minHeight: "235px",
                  cursor: "pointer",
                  border: "2px solid transparent",
                  transition:
                    "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                }}
                onClick={() => handleSelectGraph(template)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    event.key === " "
                  ) {
                    event.preventDefault();
                    handleSelectGraph(template);
                  }
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.borderColor =
                    "#0d6efd";
                  event.currentTarget.style.boxShadow =
                    "0 0.25rem 0.75rem rgba(13, 110, 253, 0.12)";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.borderColor =
                    "transparent";
                  event.currentTarget.style.boxShadow = "";
                }}
              >
                <Card.Img
                  variant="top"
                  src={template.image}
                  alt={template.label}
                  style={{
                    height: "100px",
                    width: "100%",
                    objectFit: "contain",
                    padding: "10px",
                  }}
                />

                <Card.Body className="d-flex flex-column align-items-center">
                  <h6 className="mb-2">{template.label}</h6>

                  <small className="text-muted">
                    {template.description}
                  </small>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default AddGraphModal;